import React, { useEffect, useState } from "react";
import { Modal } from "@mui/material";
import { useRecoilValue } from "recoil";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SPANISH_ABBREV, getAvailableLanguages } from "@bto/shared";
import ReportVideoEmbed from "../../components/shared/Video/ReportVideoEmbed";
import {
  DetailedReportTop,
  AneuploidyGeneralContainer,
  AneuploidyDesktopLeftWrapper,
  AneuploidyDesktopRightWrapper,
  AneuploidyDesktopWrapper,
  AneuploidyTitleWrapper,
  AneuploidyWrapper,
  AneuploidyTabletMobileWrapper,
  AneuploidyBackToResults,
} from "./Aneuploidy.styles";
import { GetTypographyContent } from "../../components/shared/Typography/Typography";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { DetailedResultsHeader } from "../../components/DetailedResults/DetailedResultsHeader";
import { DetailedResultsTable } from "../../components/DetailedResults/DetailedResultsTable";
import { DetailedResultsViewReport } from "../../components/DetailedResults/DetailedResultsViewReport";
import { DetailedResultsFetalSex } from "../../components/DetailedResults/DetailedResultsFetalSex";
import { DetailedResultsFetalSexHideButtons } from "../../components/DetailedResults/DetailedFetalSexResultsHideButtons";
import { DetailedResultsFetalSexModal } from "../../components/DetailedResults/DetailedResultsFetalSexModal";
import { DetailedResultsRhD } from "../../components/DetailedResults/DetailedResultsRhD";
import { FetalSexDownloadWarningModal } from "../../components/DetailedResults/FetalSexDownloadWarningModal";
import { FetalSexModalState } from "../../components/DetailedResults/DetailedResults.types";
import { DetailedResultsExpert } from "../../components/DetailedResults/DetailedResultsExpert";
import { DetailedResultsFAQ } from "../../components/DetailedResults/DetailedResultsFAQ";
import {
  getSpecific22q,
  getSpecificAneuploidyLowRisk,
  getSpecificAneuploidyReportID,
  getSpecificAneuploidyReportVideos,
  getSpecificAneuploidyStatus,
  getSpecificFetalSex,
  getSpecificRequisition,
  getSpecificRhD,
  getUserAuth,
  getSpecificAneuploidyPdfUrls,
} from "../../recoil/selectors";
import { BABYPEEK_URL, LANDING_PAGE_URL } from "../../utils/urlConstants";

import { BackIcon } from "../../assets/iconComponents/BackIcon";
import {
  useReportDownload,
  shouldShowAneuploidyFetalSexWarning,
} from "../../utils/hooks/useReportDownload";
import { SendActionType } from "../../components/shared/Services/SharedServices";
import { DetailedResultsShareBabyPeek } from "../../components/DetailedResults/DetailedResultsShareBabyPeek";
import BabyPeekHeader from "../../components/BabyPeek/BabyPeekIcons/BabyPeekHeader.png";
import { DetailedResults22Q } from "../../components/DetailedResults/DetailedResults22Q";
import { BabyPeekButton } from "../../components/BabyPeek/BabyPeekButton";
import { BabyPeekAvailable } from "../Landing/Landing.styles";
import TestResultsMultipleReqHeader from "../../components/TestResults/TestResultsMultipleReqHeader";
import FetalSexForwardPage from "./FetalSexForwardPage";
import {
  STANDALONE_FETAL_SEX_EXPERIENCE,
  useStandaloneFetalSexRollout,
} from "../../utils/hooks/useStandaloneFetalSexRollout";

// Maps analytics_prefix to legacy anchor IDs for backwards compatibility with existing URLs/bookmarks.
// Dashboard links (TestResults.tsx) use #aneuploidyInfoVideo and #twentyTwoQInfoVideo.
// NOTE: Only AneuploidyLowRisk/NoCall and Del22qLowRisk had dashboard shortcuts before these changes.
// NOTE: Other video types (RhD Detected/Not Detected, Zygosity, Fetal Antigen Detected/Not Detected)
// don't have "Watch video results" links in TestResults.tsx.
export const getVideoAnchorId = (analyticsPrefix: string): string => {
  const legacyMapping: Record<string, string> = {
    AneuploidyLowRiskVideo: "aneuploidyInfoVideo",
    AneuploidyNoCallVideo: "aneuploidyInfoVideo",
    AneuploidyDel22qLowRiskVideo: "twentyTwoQInfoVideo",
  };
  return legacyMapping[analyticsPrefix] || analyticsPrefix;
};

const AneuploidyPage = (): React.ReactElement => {
  const { requisitionID } = useParams<{ requisitionID: string }>();
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const history = useHistory();
  const [showFetalSexModal, setShowFetalSexModal] =
    useState<FetalSexModalState>("");
  const [showFetalSex, setShowFetalSex] = useState(false);
  const [forwardFetalSex, setForwardFetalSex] = useState(false);
  const standaloneFetalSexExperience = useStandaloneFetalSexRollout();
  const isStandaloneFetalSexEnabled =
    standaloneFetalSexExperience === STANDALONE_FETAL_SEX_EXPERIENCE.STANDALONE;
  const shouldShowLegacyFetalSex =
    standaloneFetalSexExperience === STANDALONE_FETAL_SEX_EXPERIENCE.LEGACY;
  const isStandaloneFetalSexResolved =
    standaloneFetalSexExperience !== STANDALONE_FETAL_SEX_EXPERIENCE.LOADING;
  const isLowRiskAneu = useRecoilValue(
    getSpecificAneuploidyLowRisk(requisitionID),
  );

  const requisition = useRecoilValue(getSpecificRequisition(requisitionID));
  const fetalSex = useRecoilValue(getSpecificFetalSex(requisitionID));
  const status = useRecoilValue(getSpecificAneuploidyStatus(requisitionID));
  const reportID = useRecoilValue(getSpecificAneuploidyReportID(requisitionID));
  const auth = useRecoilValue(getUserAuth);
  const rhDResults = useRecoilValue(getSpecificRhD(requisitionID));
  const del22Qstatus = useRecoilValue(getSpecific22q(requisitionID));
  const reportVideos = useRecoilValue(
    getSpecificAneuploidyReportVideos(requisitionID),
  );
  const pdfUrlsByLanguage = useRecoilValue(
    getSpecificAneuploidyPdfUrls(requisitionID),
  );
  const availableLanguages = getAvailableLanguages(pdfUrlsByLanguage);
  const aneuploidyFetalSexWarning = shouldShowAneuploidyFetalSexWarning(
    fetalSex,
    requisition?.standalone_fetal_sex_result?.standalone_fetal_sex,
    isStandaloneFetalSexEnabled,
  );

  const showFetalSexAction = async () => {
    try {
      await SendActionType("ShowFetalSex", requisitionID, auth?.access_token);
    } catch (error) {
      console.error("Failed to record legacy fetal sex reveal", { error });
    }
  };

  useEffect(() => {
    const sendUIAction = async () => {
      try {
        await SendActionType(
          "ViewAneuploidyPage",
          requisitionID,
          auth?.access_token,
          {
            result_available: String(
              Boolean(
                requisition?.aneuploidy_reports &&
                requisition?.aneuploidy_reports.length,
              ),
            ),
            report_summary: isLowRiskAneu ? "Low Risk" : "See Report",
          },
        );
      } catch (e) {
        return undefined;
      }
    };
    sendUIAction();
  }, []);

  const { downloadReport } = useReportDownload();

  useEffect(() => {
    if (auth && (!status || !(isLowRiskAneu !== undefined))) {
      history.push(LANDING_PAGE_URL);
    }
  }, [status, isLowRiskAneu, auth]);

  const statusValid =
    status && status !== "Processing" && status !== "Not Ordered";
  const shouldRenderFetalSexModal =
    isStandaloneFetalSexResolved || Boolean(showFetalSexModal);

  const ModalComponent = shouldRenderFetalSexModal ? (
    <Modal
      open={Boolean(showFetalSexModal)}
      onClose={() => setShowFetalSexModal("")}
      style={{
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      {isStandaloneFetalSexEnabled ? (
        <FetalSexDownloadWarningModal
          analyticsPageName="AneuploidyReportPage"
          onConfirmDownload={() =>
            downloadReport(
              reportID,
              "aneuploidy",
              showFetalSexModal === "report_es" ? SPANISH_ABBREV : undefined,
              availableLanguages,
            )
          }
          showModal={setShowFetalSexModal}
        />
      ) : (
        // TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout.
        <DetailedResultsFetalSexModal
          showModal={setShowFetalSexModal}
          showFetalSexModal={showFetalSexModal}
          setShowFetalSex={setShowFetalSex}
          showFetalSexAction={showFetalSexAction}
          onConfirmDownload={() =>
            downloadReport(
              reportID,
              "aneuploidy",
              showFetalSexModal === "report_es" ? SPANISH_ABBREV : undefined,
              availableLanguages,
            )
          }
        />
      )}
    </Modal>
  ) : null;

  // TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout.
  if (shouldShowLegacyFetalSex && forwardFetalSex) {
    return (
      <FetalSexForwardPage
        setForwardFetalSex={setForwardFetalSex}
        requisitionID={requisitionID}
      />
    );
  }
  if (deviceType === "desktop") {
    return (
      <>
        {ModalComponent}
        <AneuploidyWrapper>
          <AneuploidyDesktopWrapper>
            <AneuploidyDesktopLeftWrapper>
              <AneuploidyBackToResults
                onClick={() => history.push(LANDING_PAGE_URL)}
              >
                <BackIcon />
                {GetTypographyContent({
                  content: t("results.actions.backToTests"),
                  size: "medium",
                  textColor: "redMedium",
                })}
              </AneuploidyBackToResults>
              <AneuploidyTitleWrapper>
                {GetTypographyContent({
                  content: t("results.pages.aneuploidyTitle"),
                  size: "h2",
                  textColor: "greyMedium",
                  isBold: true,
                  padding: "0 0 0 10px",
                })}
              </AneuploidyTitleWrapper>
              <TestResultsMultipleReqHeader />
              <AneuploidyGeneralContainer padding="25px">
                <DetailedReportTop>
                  <DetailedResultsHeader
                    type="aneuploidy"
                    requisitionID={requisitionID}
                  />

                  {isLowRiskAneu && statusValid && (
                    <DetailedResultsTable
                      isAneuploidy
                      requisitionID={requisitionID}
                    />
                  )}
                  {statusValid && (
                    <DetailedResultsViewReport
                      fetalSexWarning={aneuploidyFetalSexWarning}
                      reportDownload={() =>
                        downloadReport(
                          reportID,
                          "aneuploidy",
                          undefined,
                          availableLanguages,
                        )
                      }
                      spanishReportDownload={
                        pdfUrlsByLanguage?.[SPANISH_ABBREV]
                          ? () =>
                              downloadReport(
                                reportID,
                                "aneuploidy",
                                SPANISH_ABBREV,
                                availableLanguages,
                              )
                          : undefined
                      }
                      setShowFetalSexModal={setShowFetalSexModal}
                    />
                  )}
                </DetailedReportTop>
              </AneuploidyGeneralContainer>

              {rhDResults !== "Not Ordered" && rhDResults !== "Opted out" && (
                <AneuploidyGeneralContainer padding="25px">
                  <DetailedResultsRhD requisitionID={requisitionID} />
                </AneuploidyGeneralContainer>
              )}

              {del22Qstatus !== "Opted out" && (
                <AneuploidyGeneralContainer padding="25px">
                  <DetailedResults22Q requisitionID={requisitionID} />
                </AneuploidyGeneralContainer>
              )}
              {Boolean(
                requisition && requisition.reveal_status === "available",
              ) && (
                <BabyPeekAvailable style={{ marginTop: "20px" }}>
                  <div>
                    <img src={BabyPeekHeader} alt="BabyPeekHeader" />
                    <div>BabyPeek</div>
                  </div>
                  <div>
                    <BabyPeekButton
                      variant="filled"
                      content={<>{t("results.actions.viewResults")}</>}
                      onClick={() =>
                        history.push(
                          `${BABYPEEK_URL}/${
                            requisition && requisition.baby_peek_pk
                          }/summary?loc=dashboard`,
                        )
                      }
                    />
                  </div>
                </BabyPeekAvailable>
              )}
              {/* TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout. */}
              {shouldShowLegacyFetalSex && (
                <AneuploidyGeneralContainer padding="25px 70px 35px 25px">
                  <DetailedResultsFetalSex
                    setShowFetalSexModal={setShowFetalSexModal}
                    showFetalSex={showFetalSex}
                    setForwardFetalSex={setForwardFetalSex}
                    requisitionID={requisitionID}
                  />
                  {showFetalSex && (
                    <DetailedResultsFetalSexHideButtons
                      setForwardFetalSex={setForwardFetalSex}
                      setShowFetalSex={setShowFetalSex}
                    />
                  )}
                </AneuploidyGeneralContainer>
              )}
              {statusValid &&
                reportVideos.map((video) => (
                  <AneuploidyGeneralContainer
                    key={video.video_url}
                    id={getVideoAnchorId(video.analytics_prefix)}
                    padding="30px 15px 30px 16px"
                  >
                    <ReportVideoEmbed
                      reportType="aneuploidy"
                      url={video.video_url}
                      analyticsPrefix={video.analytics_prefix}
                      requisitionID={requisitionID}
                    />
                  </AneuploidyGeneralContainer>
                ))}
            </AneuploidyDesktopLeftWrapper>
            <AneuploidyDesktopRightWrapper
              babyPeek={requisition?.reveal_status === "pending"}
            >
              <div />
              <div />
              {requisition?.reveal_status === "pending" && (
                <DetailedResultsShareBabyPeek
                  babyPeekPK={requisition.baby_peek_pk}
                  page="Aneuploidy"
                />
              )}

              <AneuploidyGeneralContainer padding="25px">
                <DetailedResultsExpert />
              </AneuploidyGeneralContainer>
              <AneuploidyGeneralContainer padding="25px">
                <DetailedResultsFAQ />
              </AneuploidyGeneralContainer>
            </AneuploidyDesktopRightWrapper>
          </AneuploidyDesktopWrapper>
        </AneuploidyWrapper>
      </>
    );
  }
  return (
    <>
      {ModalComponent}
      <AneuploidyWrapper>
        <AneuploidyTabletMobileWrapper deviceType={deviceType}>
          <AneuploidyBackToResults
            onClick={() => history.push(LANDING_PAGE_URL)}
          >
            <BackIcon />
            {GetTypographyContent({
              content: t("results.actions.backToTests"),
              size: "medium",
              textColor: "redMedium",
            })}
          </AneuploidyBackToResults>
          <AneuploidyTitleWrapper>
            {GetTypographyContent({
              content: t("results.pages.aneuploidyTitle"),
              size: "h2",
              textColor: "greyMedium",
              isBold: true,
              padding: "0 0 0 10px",
            })}
          </AneuploidyTitleWrapper>
          <TestResultsMultipleReqHeader />
          <AneuploidyGeneralContainer padding="16px">
            <DetailedReportTop>
              <DetailedResultsHeader
                type="aneuploidy"
                requisitionID={requisitionID}
              />
              {isLowRiskAneu && statusValid && (
                <DetailedResultsTable
                  isAneuploidy
                  requisitionID={requisitionID}
                />
              )}
              {statusValid && (
                <DetailedResultsViewReport
                  setShowFetalSexModal={setShowFetalSexModal}
                  reportDownload={() =>
                    downloadReport(
                      reportID,
                      "aneuploidy",
                      undefined,
                      availableLanguages,
                    )
                  }
                  spanishReportDownload={
                    pdfUrlsByLanguage?.[SPANISH_ABBREV]
                      ? () =>
                          downloadReport(
                            reportID,
                            "aneuploidy",
                            SPANISH_ABBREV,
                            availableLanguages,
                          )
                      : undefined
                  }
                  fetalSexWarning={aneuploidyFetalSexWarning}
                />
              )}
            </DetailedReportTop>
          </AneuploidyGeneralContainer>
          {rhDResults !== "Not Ordered" && rhDResults !== "Opted out" && (
            <AneuploidyGeneralContainer padding="25px 15px 30px 16px">
              <DetailedResultsRhD requisitionID={requisitionID} />
            </AneuploidyGeneralContainer>
          )}

          {del22Qstatus !== "Opted out" && (
            <AneuploidyGeneralContainer padding="25px">
              <DetailedResults22Q requisitionID={requisitionID} />
            </AneuploidyGeneralContainer>
          )}
          {Boolean(
            requisition && requisition.reveal_status === "available",
          ) && (
            <BabyPeekAvailable style={{ marginTop: "20px" }}>
              <div>
                <img src={BabyPeekHeader} alt="BabyPeekHeader" />
                <div>BabyPeek</div>
              </div>
              <div>
                <BabyPeekButton
                  variant="filled"
                  content={<>{t("results.actions.viewResults")}</>}
                  onClick={() =>
                    history.push(
                      `${BABYPEEK_URL}/${
                        requisition && requisition.baby_peek_pk
                      }/summary?loc=dashboard`,
                    )
                  }
                />
              </div>
            </BabyPeekAvailable>
          )}
          {requisition?.reveal_status === "pending" && (
            <DetailedResultsShareBabyPeek
              babyPeekPK={requisition.baby_peek_pk}
              page="Aneuploidy"
            />
          )}
          {/* TODO(SFS_FLAG_CLEANUP): Remove the legacy fetal-sex path after rollout. */}
          {shouldShowLegacyFetalSex && (
            <AneuploidyGeneralContainer padding="30px 15px 25px 16px">
              <DetailedResultsFetalSex
                setShowFetalSexModal={setShowFetalSexModal}
                showFetalSex={showFetalSex}
                setForwardFetalSex={setForwardFetalSex}
                requisitionID={requisitionID}
              />
              {showFetalSex && (
                <DetailedResultsFetalSexHideButtons
                  setForwardFetalSex={setForwardFetalSex}
                  setShowFetalSex={setShowFetalSex}
                />
              )}
            </AneuploidyGeneralContainer>
          )}
          {statusValid &&
            reportVideos.map((video) => (
              <AneuploidyGeneralContainer
                key={video.video_url}
                id={getVideoAnchorId(video.analytics_prefix)}
                padding="30px 15px 30px 16px"
              >
                <ReportVideoEmbed
                  reportType="aneuploidy"
                  url={video.video_url}
                  analyticsPrefix={video.analytics_prefix}
                  requisitionID={requisitionID}
                />
              </AneuploidyGeneralContainer>
            ))}
          <div>
            <AneuploidyGeneralContainer padding="25px">
              <DetailedResultsExpert />
            </AneuploidyGeneralContainer>
            <AneuploidyGeneralContainer padding="16px">
              <DetailedResultsFAQ />
            </AneuploidyGeneralContainer>
          </div>
        </AneuploidyTabletMobileWrapper>
      </AneuploidyWrapper>
    </>
  );
};

export default AneuploidyPage;
